import React, { useCallback, useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, GraduationCap, BookOpen, Award, Handshake, Clock, BarChart3, Lock, ShieldCheck, RefreshCw, Loader2, Copy } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { trainingTracks, certifications, dealStats } from '../data/portalContent';
import DealRegistrationDialog from '../components/DealRegistrationDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const STATUS_CLASS = {
  pending_approval: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
  approved: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  rejected: 'bg-red-500/15 text-red-300 border-red-500/40',
};
const STATUS_LABEL = {
  pending_approval: 'Pending approval',
  approved: 'Approved',
  rejected: 'Rejected',
};

const MyDealsList = ({ refreshSignal, onRegisterClick }) => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copiedCode, setCopiedCode] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/deals/mine`, { withCredentials: true });
      setDeals(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not load your deals.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshSignal]);

  const copy = async (code) => {
    try { await navigator.clipboard.writeText(code); setCopiedCode(code); setTimeout(() => setCopiedCode(''), 1500); } catch { /* noop */ }
  };

  return (
    <div className="mt-12">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-5">
        <div>
          <p className="text-[12px] uppercase tracking-[0.18em] text-neutral-500">My deal registrations</p>
          <h3 className="mt-2 text-[22px] lg:text-[26px] leading-tight font-semibold text-white">
            Track every deal you have submitted
          </h3>
          <p className="mt-1.5 text-[13.5px] text-neutral-400 max-w-xl">
            Status updates the moment our sales team approves or rejects. Approved deals are protected at the listed pricing.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={load} size="sm" variant="outline" className="border-neutral-800 bg-transparent text-white hover:bg-neutral-900 h-9">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
          <Button onClick={onRegisterClick} size="sm" className="bg-white text-neutral-950 hover:bg-neutral-200 h-9">
            Register a deal
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="rounded-2xl border border-neutral-800 bg-neutral-950 p-10 flex justify-center">
          <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
        </div>
      ) : error ? (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">{error}</div>
      ) : deals.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-neutral-800 bg-neutral-950/60 p-10 text-center">
          <Handshake className="w-6 h-6 text-neutral-600 mx-auto" />
          <p className="mt-3 text-[14px] text-neutral-400">You have not registered any deals yet.</p>
          <Button onClick={onRegisterClick} className="mt-5 h-10 px-5 rounded-full bg-white text-neutral-950 hover:bg-neutral-200 text-[13.5px] font-medium">
            Start your first registration
          </Button>
        </div>
      ) : (
        <div className="rounded-2xl border border-neutral-800 bg-neutral-950 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[13.5px]">
              <thead className="bg-neutral-900/60 text-neutral-400 text-[11.5px] uppercase tracking-wider">
                <tr>
                  <th className="px-5 py-3 font-medium">Deal</th>
                  <th className="px-5 py-3 font-medium">Customer</th>
                  <th className="px-5 py-3 font-medium">Product · Qty</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Decision</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-900">
                {deals.map((d) => (
                  <tr key={d.id} className="hover:bg-neutral-900/50 align-top">
                    <td className="px-5 py-3">
                      <div className="inline-flex items-center gap-2">
                        <code className="text-blue-400 text-[12.5px]">{d.deal_code}</code>
                        <button onClick={() => copy(d.deal_code)} className="text-neutral-500 hover:text-white" title="Copy">
                          <Copy className="w-3 h-3" />
                        </button>
                        {copiedCode === d.deal_code && <span className="text-[10.5px] text-blue-400">Copied</span>}
                      </div>
                      <div className="text-[11.5px] text-neutral-500 mt-1">{fmtDate(d.submitted_at)}</div>
                    </td>
                    <td className="px-5 py-3 text-white">{d.customer_name}</td>
                    <td className="px-5 py-3 text-neutral-300">
                      {d.product}
                      <span className="ml-1 text-neutral-500">· {d.quantity}</span>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] ${STATUS_CLASS[d.status] || 'bg-neutral-700/30 text-neutral-300 border-neutral-700'}`}>
                        {STATUS_LABEL[d.status] || d.status}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      {d.decision_at ? (
                        <div className="text-[12.5px] text-neutral-300">
                          {fmtDate(d.decision_at)}
                          {d.decision_by && <div className="text-[11.5px] text-neutral-500">by {d.decision_by}</div>}
                          {d.decision_note && <div className="text-[11.5px] text-neutral-400 italic mt-1 max-w-xs">“{d.decision_note}”</div>}
                        </div>
                      ) : (
                        <span className="text-[12.5px] text-neutral-500">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// `audience`: 'partner' | 'employee'
const PortalPage = ({ audience }) => {
  const [activeTab, setActiveTab] = useState('training');
  const [dealsRefreshSignal, setDealsRefreshSignal] = useState(0);
  const [dealOpen, setDealOpen] = useState(false);
  const trainingRef = useRef(null);
  const certRef = useRef(null);
  const dealRef = useRef(null);

  useEffect(() => { window.scrollTo(0, 0); }, [audience]);

  const scrollTo = (id) => {
    setActiveTab(id);
    const map = { training: trainingRef, certification: certRef, 'deal-registration': dealRef };
    const el = map[id]?.current;
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 110;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  // Update active tab on scroll
  useEffect(() => {
    const onScroll = () => {
      const offset = 160;
      const positions = [
        { id: 'training', top: trainingRef.current?.offsetTop ?? 0 },
        { id: 'certification', top: certRef.current?.offsetTop ?? 0 },
        { id: 'deal-registration', top: dealRef.current?.offsetTop ?? 0 },
      ];
      const y = window.scrollY + offset;
      let current = 'training';
      for (const p of positions) if (y >= p.top) current = p.id;
      setActiveTab(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const labels = {
    partner: {
      eyebrow: 'Partner Portal',
      title: 'Win more. Deliver faster.',
      tagline: 'Your home for MetaxuOne enablement, certification, and deal registration.',
      portalKey: 'partner',
    },
    employee: {
      eyebrow: 'Employee Portal',
      title: 'Built for MetaxuOne teams.',
      tagline: 'Onboard, certify, and register opportunities — all in one place.',
      portalKey: 'employee',
    },
  }[audience];

  const tracks = trainingTracks[audience];
  const certs = certifications[audience];

  return (
    <div className="bg-black min-h-screen">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative overflow-hidden bg-black border-b border-neutral-900">
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(60% 50% at 15% 0%, rgba(59,130,246,0.22), transparent), radial-gradient(50% 60% at 90% 30%, rgba(255,255,255,0.05), transparent)',
            }}
          />
          <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 pb-12">
            <nav className="flex items-center gap-1.5 text-[12.5px] text-neutral-500">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-neutral-300">{labels.eyebrow}</span>
            </nav>
            <p className="mt-6 text-[13px] uppercase tracking-[0.18em] text-blue-400">{labels.eyebrow}</p>
            <h1 className="mt-3 text-[42px] sm:text-[56px] lg:text-[68px] leading-[1.02] tracking-[-0.025em] font-semibold text-white max-w-3xl">
              {labels.title}
            </h1>
            <p className="mt-5 text-[16px] lg:text-[17px] text-neutral-300 max-w-2xl leading-relaxed">
              {labels.tagline}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button
                onClick={() => scrollTo('training')}
                className="h-11 px-5 rounded-full bg-white hover:bg-neutral-200 text-neutral-950 text-[14px] font-medium inline-flex items-center gap-2"
              >
                Explore training <ArrowRight className="w-4 h-4" />
              </Button>
              <Button
                onClick={() => scrollTo('deal-registration')}
                variant="outline"
                className="h-11 px-5 rounded-full border-neutral-700 bg-transparent text-white hover:bg-neutral-900 text-[14px] font-medium"
              >
                Register a deal
              </Button>
            </div>
          </div>
        </section>

        {/* Sticky tabs */}
        <div className="sticky top-16 z-40 bg-black/85 backdrop-blur border-b border-neutral-900">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="flex gap-1 overflow-x-auto">
              {[
                { id: 'training', label: 'Training', icon: BookOpen },
                { id: 'certification', label: 'Certification', icon: Award },
                { id: 'deal-registration', label: 'Deal Registration', icon: Handshake },
              ].map((t) => {
                const Icon = t.icon;
                const isActive = activeTab === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => scrollTo(t.id)}
                    className={`relative inline-flex items-center gap-2 px-4 py-3.5 text-[14px] font-medium whitespace-nowrap transition-colors ${isActive ? 'text-white' : 'text-neutral-400 hover:text-white'}`}
                  >
                    <Icon className="w-4 h-4" />
                    {t.label}
                    {isActive && <span className="absolute left-3 right-3 bottom-0 h-[2px] bg-blue-500 rounded-full" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Training */}
        <section id="training" ref={trainingRef} className="bg-black scroll-mt-28">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
              <div>
                <p className="text-[13px] uppercase tracking-[0.18em] text-neutral-500">Training</p>
                <h2 className="mt-3 text-[36px] lg:text-[48px] leading-[1.02] tracking-[-0.02em] font-semibold text-white max-w-2xl">
                  Learning paths designed for <span className="italic font-light text-neutral-400">{audience === 'employee' ? 'every MetaxuOne role' : 'partner success'}.</span>
                </h2>
                <p className="mt-4 text-[15px] text-neutral-400 max-w-2xl leading-relaxed">
                  Structured curricula, hands-on labs, and real-world scenarios — built and maintained by MetaxuOne engineers, architects, and product leaders.
                </p>
              </div>
              <div className="flex items-center gap-4 text-[13px] text-neutral-400">
                <div className="flex items-center gap-1.5"><BookOpen className="w-4 h-4" />{tracks.length} tracks</div>
                <div className="h-3 w-px bg-neutral-800" />
                <div className="flex items-center gap-1.5"><Clock className="w-4 h-4" />Self-paced</div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {tracks.map((t) => {
                const Icon = t.icon;
                return (
                  <article key={t.id} className="group rounded-2xl border border-neutral-800 bg-neutral-950 p-6 hover:border-blue-500 hover:-translate-y-0.5 transition-all">
                    <div className="flex items-center justify-between">
                      <div className="w-11 h-11 rounded-xl bg-white text-neutral-950 flex items-center justify-center">
                        <Icon className="w-5 h-5" />
                      </div>
                      <span className="text-[11px] uppercase tracking-wider text-neutral-500">{t.level}</span>
                    </div>
                    <p className="mt-5 text-[11.5px] uppercase tracking-wider text-blue-400">{t.tag}</p>
                    <h3 className="mt-1.5 text-[17px] font-semibold text-white leading-snug">{t.title}</h3>
                    <p className="mt-2 text-[13.5px] text-neutral-400 leading-relaxed">{t.description}</p>
                    <div className="mt-5 pt-4 border-t border-neutral-900 flex items-center justify-between">
                      <div className="inline-flex items-center gap-1.5 text-[12px] text-neutral-400">
                        <Clock className="w-3.5 h-3.5" /> {t.duration}
                      </div>
                      <span className="inline-flex items-center gap-1 text-[13px] text-white opacity-80 group-hover:opacity-100">
                        Start <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                      </span>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* Certification */}
        <section id="certification" ref={certRef} className="bg-[#0a0a0a] scroll-mt-28">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
              <div>
                <p className="text-[13px] uppercase tracking-[0.18em] text-neutral-500">Certification</p>
                <h2 className="mt-3 text-[36px] lg:text-[48px] leading-[1.02] tracking-[-0.02em] font-semibold text-white max-w-2xl">
                  Prove your expertise with <span className="italic font-light text-neutral-400">industry-recognized credentials.</span>
                </h2>
                <p className="mt-4 text-[15px] text-neutral-400 max-w-2xl leading-relaxed">
                  Earn digital badges that validate your MetaxuOne skills — across foundational, specialist, and expert levels.
                </p>
              </div>
              <div className="flex items-center gap-4 text-[13px] text-neutral-400">
                <div className="flex items-center gap-1.5"><GraduationCap className="w-4 h-4" />{certs.length} certifications</div>
                <div className="h-3 w-px bg-neutral-800" />
                <div className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" />Proctored online</div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {certs.map((c) => {
                const Icon = c.icon;
                return (
                  <article key={c.id} className="group rounded-2xl border border-neutral-800 bg-neutral-950 p-6 hover:border-blue-500 transition-all flex gap-5">
                    <div className={`flex-shrink-0 w-16 h-16 rounded-2xl ${c.badge} flex items-center justify-center shadow-[0_8px_24px_-12px_rgba(59,130,246,0.6)]`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="px-2 py-0.5 rounded-full bg-blue-500/15 border border-blue-500/30 text-[10.5px] uppercase tracking-wider text-blue-300">{c.level}</span>
                      </div>
                      <h3 className="text-[18px] font-semibold text-white leading-snug">{c.title}</h3>
                      <p className="mt-1.5 text-[13.5px] text-neutral-400 leading-relaxed">{c.description}</p>
                      <div className="mt-4 grid grid-cols-2 gap-3 text-[12px]">
                        <div>
                          <p className="text-neutral-500 uppercase tracking-wider text-[10.5px] mb-0.5">Format</p>
                          <p className="text-neutral-300">{c.duration}</p>
                        </div>
                        <div>
                          <p className="text-neutral-500 uppercase tracking-wider text-[10.5px] mb-0.5">Prerequisite</p>
                          <p className="text-neutral-300">{c.requires}</p>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* Deal Registration */}
        <section id="deal-registration" ref={dealRef} className="bg-black scroll-mt-28">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
            <div className="grid lg:grid-cols-12 gap-10">
              <div className="lg:col-span-7">
                <p className="text-[13px] uppercase tracking-[0.18em] text-neutral-500">Deal Registration</p>
                <h2 className="mt-3 text-[36px] lg:text-[52px] leading-[1.02] tracking-[-0.02em] font-semibold text-white">
                  Lock in the deal. <span className="italic font-light text-neutral-400">Get rewarded.</span>
                </h2>
                <p className="mt-5 text-[16px] text-neutral-300 leading-relaxed max-w-xl">
                  Register a qualified opportunity to receive deal protection, preferred pricing, and dedicated support from the MetaxuOne sales team.
                </p>

                <div className="mt-8 grid sm:grid-cols-3 gap-3">
                  {dealStats.map((s) => (
                    <div key={s.label} className="rounded-xl border border-neutral-800 bg-neutral-950 p-4 hover:border-blue-500 transition-colors">
                      <div className="text-[24px] font-semibold text-white tracking-tight">{s.value}</div>
                      <div className="mt-1.5 text-[12px] text-neutral-400 leading-snug">{s.label}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 flex flex-wrap gap-3">
                  <Button
                    onClick={() => setDealOpen(true)}
                    className="h-12 px-6 rounded-full bg-white hover:bg-neutral-200 text-neutral-950 text-[14px] font-medium inline-flex items-center gap-2"
                  >
                    Start Deal Registration <ArrowRight className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    className="h-12 px-6 rounded-full border-neutral-700 bg-transparent text-white hover:bg-neutral-900 text-[14px] font-medium"
                  >
                    Program guide
                  </Button>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="rounded-2xl border border-neutral-800 bg-neutral-950 p-7">
                  <p className="text-[12px] uppercase tracking-[0.18em] text-blue-400">How it works</p>
                  <ol className="mt-5 space-y-5">
                    {[
                      { icon: Handshake, title: 'Submit the opportunity', text: 'Provide the customer, product, quantity, and your contact details.' },
                      { icon: ShieldCheck, title: 'Get approval', text: 'Our sales team reviews and approves within 2 business days.' },
                      { icon: BarChart3, title: 'Close & earn', text: 'Enjoy protected pricing, sales support, and partner rewards.' },
                    ].map(({ icon: Icon, title, text }, i) => (
                      <li key={title} className="flex gap-4">
                        <div className="flex-shrink-0 w-9 h-9 rounded-full bg-blue-500/15 border border-blue-500/30 text-blue-300 flex items-center justify-center text-[12px] font-semibold">
                          0{i + 1}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4 text-blue-400" />
                            <h4 className="text-[15px] font-semibold text-white">{title}</h4>
                          </div>
                          <p className="mt-1 text-[13px] text-neutral-400 leading-relaxed">{text}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                  <div className="mt-7 pt-5 border-t border-neutral-900 flex items-center gap-2 text-[12px] text-neutral-500">
                    <Lock className="w-3.5 h-3.5" /> Submissions are encrypted and shared only with MetaxuOne sales.
                  </div>
                </div>
              </div>
            </div>

            <MyDealsList
              refreshSignal={dealsRefreshSignal}
              onRegisterClick={() => setDealOpen(true)}
            />
          </div>
        </section>
      </main>
      <Footer />

      <DealRegistrationDialog
        open={dealOpen}
        onOpenChange={(next) => {
          setDealOpen(next);
          // Bump the signal when the dialog closes so the list refetches in case a new deal was just submitted.
          if (!next) setDealsRefreshSignal((s) => s + 1);
        }}
        portal={labels.portalKey}
      />
    </div>
  );
};

export default PortalPage;
